import axios, { AxiosError, type AxiosRequestConfig } from 'axios'

import { appEnv } from '@/config/env'
import type { ApiEnvelope } from '@/types/api'

import { resolveAssetUrlsDeep } from './content'
import { clearPortalSession, readPortalSession } from './storage'

export interface PortalRequestConfig extends AxiosRequestConfig {
  skipPortalAuth?: boolean
}

const createRequestId = () => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
}

const http = axios.create({
  baseURL: appEnv.apiBaseUrl,
  timeout: 15_000,
  withCredentials: true,
})

http.interceptors.request.use((config) => {
  const portalConfig = config as typeof config & PortalRequestConfig
  if (!portalConfig.skipPortalAuth) {
    const session = readPortalSession()
    if (session?.accessToken) {
      config.headers.Authorization = `Bearer ${session.accessToken}`
    }
  }
  delete portalConfig.skipPortalAuth
  config.headers['x-client-type'] = 'member-h5'
  config.headers['x-request-id'] = createRequestId()
  return config
})

const handleUnauthorized = () => {
  clearPortalSession()
  window.dispatchEvent(new CustomEvent('member-portal:unauthorized'))
}

http.interceptors.response.use(
  (response) => {
    const envelope = response.data as ApiEnvelope<unknown>
    if (envelope && typeof envelope === 'object' && 'successful' in envelope) {
      if (envelope.successful || envelope.code === 200) {
        return resolveAssetUrlsDeep(envelope.data)
      }
      if (envelope.code === 401) handleUnauthorized()
      return Promise.reject(new Error(envelope.message || '请求失败'))
    }
    return resolveAssetUrlsDeep(response.data)
  },
  (error: AxiosError<ApiEnvelope<unknown>>) => {
    if (error.response?.status === 401) {
      handleUnauthorized()
    }

    const message = error.response?.data?.message || error.message || '网络请求失败'
    return Promise.reject(new Error(message))
  },
)

export const request = <T>(config: PortalRequestConfig): Promise<T> =>
  // The response interceptor unwraps ApiEnvelope<T> before this promise resolves.
  http.request<ApiEnvelope<T>>(config) as unknown as Promise<T>
